import { useMemo } from 'react';
import type { PackEntry } from 'types/shared';
import type { HypothesisCreativeEntry } from '../types/hypothesis';
import {
  MIXED_PACK_KEY,
  packHypothesisKeyFromPack,
  shouldHideCreativeFromUnpacked,
} from '../utils/hypothesisPackUtils';

export const NO_HYPOTHESIS_KEY = 'No hypothesis';
export { MIXED_PACK_KEY };

export interface HypothesisPack {
  packIndex: number;
  tag: string;
  creatives: HypothesisCreativeEntry[];
}

type HypothesisKey = string;

interface UseCreativesByPackReturn {
  packsByHypothesis: Map<HypothesisKey, HypothesisPack[]>;
  unpackedCreatives: HypothesisCreativeEntry[];
  packedIds: Set<string>;
}

const useCreativesByPack = (
  packsWithCreatives: PackEntry[],
  packedCreatives: HypothesisCreativeEntry[],
  displayCreatives: HypothesisCreativeEntry[],
): UseCreativesByPackReturn => {
  return useMemo(() => {
    const resolveById = new Map<string, HypothesisCreativeEntry>();
    for (const c of packedCreatives) {
      resolveById.set(c.id, c);
    }
    for (const c of displayCreatives) {
      if (!resolveById.has(c.id)) {
        resolveById.set(c.id, c);
      }
    }

    const packsByHypothesis = new Map<HypothesisKey, HypothesisPack[]>();
    const packedIds = new Set<string>();

    packsWithCreatives.forEach((pack, packIndex) => {
      const ownerKey = packHypothesisKeyFromPack(pack);
      const packCreatives: HypothesisCreativeEntry[] = [];

      for (const id of pack.creatives) {
        if (typeof id !== 'string') continue;
        packedIds.add(id);
        const creative = resolveById.get(id);
        if (!creative) continue;
        packCreatives.push(creative);
      }

      const entry: HypothesisPack = {
        packIndex,
        tag: pack.tag,
        creatives: packCreatives,
      };
      const list = packsByHypothesis.get(ownerKey);
      if (list) list.push(entry);
      else packsByHypothesis.set(ownerKey, [entry]);
    });

    const unpackedCreatives = displayCreatives.filter(
      (creative) => !shouldHideCreativeFromUnpacked(creative, packsWithCreatives),
    );

    return { packsByHypothesis, unpackedCreatives, packedIds };
  }, [packsWithCreatives, packedCreatives, displayCreatives]);
};

export default useCreativesByPack;
